import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import './AdvancedBodyweightSelection.css';

const AdvancedBodyweightSelection = () => {
    const navigate = useNavigate();
    const [userInput, setUserInput] = useState('');

    // Advanced bodyweight workout presets (from Android code)
    const advancedWorkouts = [
        { code: "ekekh", name: "chest back chest back shoulder" },
        { code: "mittq", name: "arm bicep tricep tricep quad" },
        { code: "gavin", name: "legs abs calves bicep hamstring" },
        { code: "efavpq", name: "chest forearm abs calves trap quad" },
        { code: "az!", name: "abs oblique overall" },
        { code: "keen!", name: "back chest chest hamstring overall" },
        { code: "mite", name: "arm bicep tricep chest" },
        { code: "gavinfate", name: "legs abs calves bicep forearm abs tricep chest" },
        { code: "mikej", name: "arm bicep back chest hip" },
        { code: "jfit", name: "bicep forearm bicep tricep" },
        { code: "vhain", name: "calves shoulder abs bicep hamstring" },
        { code: "nuj!", name: "hamstring glute hip overall" },
        { code: "!kek!", name: "overall back chest back overall" },
        { code: "numnum", name: "hamstring glute arm hamstring glute arm" },
        { code: "vaqej", name: "calves abs quad chest hip" },
        { code: "nugaq", name: "hamstring glute legs abs quad" },
        { code: "neet!", name: "hamstring chest chest tricep overall" },
        { code: "fittif", name: "forearm bicep tricep tricep bicep forearm" },
        { code: "gavufit", name: "legs abs calves glute forearm bicep tricep" },
        { code: "aqjy", name: "abs legs hip thigh" },
        { code: "nuz!", name: "hamstring glute oblique overall" },
        { code: "vain!", name: "calves abs bicep hamstring overall" }
    ];

    // Muscle groups mapping (from Android app)
    const muscleGroups = [
        { name: "chest", code: "e" },
        { name: "back", code: "k" },
        { name: "shoulder", code: "h" },
        { name: "arm", code: "m" },
        { name: "bicep", code: "i" },
        { name: "tricep", code: "t" },
        { name: "forearm", code: "f" },
        { name: "legs", code: "g" },
        { name: "abs", code: "a" },
        { name: "calves", code: "v" },
        { name: "trap", code: "p" },
        { name: "quad", code: "q" },
        { name: "hamstring", code: "n" },
        { name: "glute", code: "u" },
        { name: "hip", code: "j" },
        { name: "thigh", code: "y" },
        { name: "oblique", code: "z" },
        { name: "overall", code: "!" }
    ];

    const allowedChars = "ekhmitfgavpqnujyz!";

    // Handle adding character to input
    const addCharacter = (char) => {
        const newInput = userInput + char;
        const lastThreeChars = newInput.slice(-3);

        // Prevent three consecutive same characters
        if (lastThreeChars.length < 3 || !(lastThreeChars[0] === lastThreeChars[1] && lastThreeChars[1] === lastThreeChars[2])) {
            setUserInput(newInput);
        }
    };

    // Handle input change
    const handleInputChange = (e) => {
        const input = e.target.value.toLowerCase();
        const validInput = input.split('').filter(char => allowedChars.includes(char)).join('');

        // Check for three consecutive same characters
        let result = '';
        for (let i = 0; i < validInput.length; i++) {
            const char = validInput[i];
            const lastThreeChars = result.slice(-2) + char;
            if (lastThreeChars.length < 3 || !(lastThreeChars[0] === lastThreeChars[1] && lastThreeChars[1] === lastThreeChars[2])) {
                result += char;
            }
        }

        setUserInput(result);
    };

    // Handle preset workout selection
    const handlePresetSelect = (workoutCode) => {
        setUserInput(workoutCode);
    };

    // Handle next button with URL encoding fix
    const handleNext = () => {
        if (userInput.trim() !== '') {
            // Navigate with properly encoded workout code
            const encodedWorkoutCode = encodeURIComponent(userInput);
            navigate(`/advanced-bodyweight-rep-count/${encodedWorkoutCode}`, {
                state: {
                    workoutCode: userInput, // Pass unencoded version in state
                    workoutType: 'advanced-bodyweight'
                }
            });
        }
    };

    // Generate random background image
    const getRandomBackground = () => {
        const randomIndex = Math.floor(Math.random() * 88) + 1;
        return `url(/assets/images/v${randomIndex}.jpg)`;
    };

    return (
        <div
            className="advanced-bodyweight-selection-container"
            style={{
                backgroundImage: getRandomBackground(),
                backgroundSize: 'cover',
                backgroundPosition: 'center'
            }}
        >
            {/* Dark overlay */}
            <div className="advanced-bodyweight-overlay"></div>

            {/* Fixed top bar */}
            <div className="advanced-bodyweight-top-bar">
                <button
                    className="advanced-bodyweight-back-button"
                    onClick={() => navigate('/')}
                >
                    ← Back
                </button>

                <input
                    type="text"
                    className="advanced-bodyweight-input"
                    value={userInput}
                    onChange={handleInputChange}
                    placeholder="Enter advanced code..."
                />

                <button
                    className="advanced-bodyweight-next-button"
                    onClick={handleNext}
                    disabled={userInput.trim() === ''}
                >
                    Next 🚀
                </button>
            </div>

            {/* Scrollable content */}
            <div className="advanced-bodyweight-content">
                <div className="advanced-bodyweight-header">
                    <h1>🚀 ADVANCED BODYWEIGHT 🚀</h1>
                    <p>Elite-level bodyweight mastery training!</p>
                </div>

                {/* Muscle group buttons */}
                <div className="muscle-groups-grid">
                    {muscleGroups.map((group) => (
                        <button
                            key={group.code}
                            className="muscle-group-button"
                            onClick={() => addCharacter(group.code)}
                        >
                            <span className="muscle-code">{group.code}</span>
                            <span className="muscle-name">{group.name}</span>
                        </button>
                    ))}
                </div>

                {/* Character selector */}
                <div className="character-selector">
                    <h3>Quick Character Input:</h3>
                    <div className="character-grid">
                        {allowedChars.split('').map((char) => (
                            <button
                                key={char}
                                className="character-button"
                                onClick={() => addCharacter(char)}
                            >
                                {char}
                            </button>
                        ))}
                    </div>
                </div>

                {/* Preset advanced bodyweight workouts */}
                <div className="preset-workouts">
                    <h3>⚡ Elite Performance Presets ⚡</h3>
                    <div className="preset-workouts-grid">
                        {advancedWorkouts.map((workout, index) => (
                            <div
                                key={`${workout.code}-${index}`}
                                className="preset-workout-card"
                                onClick={() => handlePresetSelect(workout.code)}
                            >
                                <div className="preset-code">{workout.code}</div>
                                <div className="preset-name">{workout.name}</div>
                            </div>
                        ))}
                    </div>
                </div>

                <div className="advanced-bodyweight-instructions">
                    <p>🚀 Master advanced movement patterns!</p>
                    <p>⚡ Elite bodyweight skills and progressions</p>
                    <p>🎯 Push your limits with expert-level training!</p>
                </div>
            </div>
        </div>
    );
};

export default AdvancedBodyweightSelection;